import React, { useEffect, useState } from "react";
import { ClipboardList, Check, CalendarPlus, AlertTriangle } from "@/components/icons";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { getErrorMessage } from "@/lib/errors";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { PuntQuickDialog } from "@/components/PuntQuickDialog";

// Loose action items for one location. Grey = "noted, no date". Built as a
// sibling block of the other location panels, placed high so it's seen on site.
export const PuntenPanel = ({ locationId, locationName }) => {
  const { canWrite } = useAuth();
  const writable = canWrite();
  const [punten, setPunten] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [scheduleFor, setScheduleFor] = useState(null);
  const [dueDate, setDueDate] = useState("");
  const [dueTime, setDueTime] = useState("");
  const [faultFor, setFaultFor] = useState(null);
  const [priority, setPriority] = useState("P3");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/punten?status=open&location_id=${locationId}`);
      setPunten(data);
    } catch (e) { /* silent */ } finally { setLoading(false); }
  };
  useEffect(() => { if (locationId) load(); /* eslint-disable-next-line */ }, [locationId]);

  const [ticked, setTicked] = useState({});
  const tick = async (p) => {
    setTicked((prev) => ({ ...prev, [p.id]: true }));
    try {
      await api.post(`/punten/${p.id}/complete`, {});
      toast.success("Punt afgevinkt");
      setPunten((prev) => prev.filter((x) => x.id !== p.id));
    } catch (e) {
      setTicked((prev) => { const n = { ...prev }; delete n[p.id]; return n; });
      toast.error(getErrorMessage(e, "Mislukt."));
    }
  };

  const doSchedule = async () => {
    if (!dueDate) { toast.error("Kies een datum."); return; }
    setBusy(true);
    try {
      await api.post(`/punten/${scheduleFor.id}/schedule`, { due_date: dueDate, due_time: dueTime || null });
      toast.success("In de agenda gezet");
      setScheduleFor(null); setDueDate(""); setDueTime("");
      await load();
    } catch (e) { toast.error(getErrorMessage(e, "Mislukt.")); } finally { setBusy(false); }
  };

  const doToFault = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/punten/${faultFor.id}/to-fault`, { priority, location_id: locationId });
      toast.success(`Storing aangemaakt: ${data.fault_number}`);
      setFaultFor(null); setPriority("P3");
      await load();
    } catch (e) { toast.error(getErrorMessage(e, "Mislukt.")); } finally { setBusy(false); }
  };

  return (
    <Card className="p-5" data-testid="punten-panel">
      <div className="mb-3 flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="flex items-center gap-2 text-body font-semibold text-foreground">
          <span className="flex h-6 w-6 items-center justify-center rounded-md bg-rust-vlak text-inkt-zacht"><ClipboardList size={18} /></span>
          Punten ({punten.length})
        </h2>
        {writable && (
          <Button variant="outline" size="sm" data-testid="punten-panel-add" onClick={() => setAddOpen(true)}>Punt toevoegen</Button>
        )}
      </div>

      {loading ? (
        <p className="text-body text-muted-foreground">Laden...</p>
      ) : punten.length === 0 ? (
        <p className="text-body text-muted-foreground">
          Hier komen de losse punten die iemand op deze locatie opmerkt — een tikkende pomp, een losse
          plaat — voordat het een echte storing wordt.
        </p>
      ) : (
        <ul className="divide-y divide-border">
          {punten.map((p) => (
            <li key={p.id} className="flex items-center gap-2 py-2" data-testid={`punt-row-${p.id}`}>
              {writable && !p.converted_fault_id && (
                <button
                  type="button"
                  data-testid={`punt-toggle-${p.id}`}
                  onClick={() => tick(p)}
                  className={`flex h-7 w-7 shrink-0 items-center justify-center rounded border-2 transition-colors duration-150 ${
                    ticked[p.id]
                      ? "border-goed-rand bg-goed-vlak text-goed-tekst"
                      : "border-lijn-sterk text-lijn-sterk hover:border-goed-rand hover:bg-goed-vlak hover:text-goed-tekst"
                  }`}
                  aria-label="Afvinken"
                >
                  <Check size={16} weight={ticked[p.id] ? "bold" : "regular"} />
                </button>
              )}
              <span className="flex-1 text-body text-foreground">{p.title}</span>
              {p.converted_fault_id ? (
                <span className="shrink-0 rounded-sm border border-letop-rand bg-letop-vlak px-2 py-0.5 text-label font-semibold uppercase tracking-[0.07em] text-letop-tekst" data-testid={`punt-converted-${p.id}`}>Storing aangemaakt</span>
              ) : (writable && (
                <div className="flex shrink-0 gap-1">
                  <Button variant="ghost" size="sm" aria-label="Plan in" data-testid={`punt-schedule-${p.id}`} onClick={() => { setScheduleFor(p); setDueDate(""); setDueTime(""); }}>
                    <CalendarPlus size={16} className="sm:mr-1" /> <span className="hidden sm:inline">Plan in</span>
                  </Button>
                  <Button variant="ghost" size="sm" aria-label="Is een storing" className="text-letop-tekst" data-testid={`punt-to-fault-${p.id}`} onClick={() => { setFaultFor(p); setPriority("P3"); }}>
                    <AlertTriangle size={16} className="sm:mr-1" /> <span className="hidden sm:inline">Is een storing</span>
                  </Button>
                </div>
              ))}
            </li>
          ))}
        </ul>
      )}

      <PuntQuickDialog open={addOpen} onOpenChange={setAddOpen} presetLocationId={locationId} presetLocationName={locationName} onSaved={load} />

      {/* Plan in */}
      <Dialog open={!!scheduleFor} onOpenChange={(o) => !o && setScheduleFor(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Plan in</DialogTitle></DialogHeader>
          <DialogDescription>Met een datum wordt dit een agenda-afspraak.</DialogDescription>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Datum</Label><Input type="date" data-testid="punt-schedule-date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Tijd (optioneel)</Label><Input type="time" value={dueTime} onChange={(e) => setDueTime(e.target.value)} /></div>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setScheduleFor(null)}>Annuleren</Button>
            <Button data-testid="punt-schedule-confirm" onClick={doSchedule} disabled={busy}>In agenda zetten</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Is een storing */}
      <Dialog open={!!faultFor} onOpenChange={(o) => !o && setFaultFor(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Omzetten naar storing</DialogTitle></DialogHeader>
          <DialogDescription>Dit maakt een echte storing aan (met storingsnummer) en sluit het punt.</DialogDescription>
          <div className="space-y-1.5">
            <Label>Prioriteit</Label>
            <Select value={priority} onValueChange={setPriority}>
              <SelectTrigger data-testid="punt-fault-priority"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="P1">P1 — kritiek (baan stil)</SelectItem>
                <SelectItem value="P2">P2 — hoog</SelectItem>
                <SelectItem value="P3">P3 — normaal</SelectItem>
                <SelectItem value="P4">P4 — laag</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setFaultFor(null)}>Annuleren</Button>
            <Button data-testid="punt-fault-confirm" onClick={doToFault} disabled={busy}>Storing aanmaken</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};
